import React, { createContext, useContext, useState, useEffect } from "react";

interface FeaturePermission {
  feature: string;
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

interface PermissionsContextType {
  canView: (feature: string) => boolean;
  canEdit: (feature: string) => boolean;
  canDelete: (feature: string) => boolean;
  /** True when the company's plan/master enables this module (ignores role ACL). */
  hasFeature: (feature: string) => boolean;
  /**
   * وحدةٌ **مدفوعة**: لا تُعرض إلا إن اشترتها الشركة صراحةً.
   *
   * `hasFeature` يقول «مفعّلةٌ ما لم تُطفأ» — وهو الصواب لوحدةٍ مشمولةٍ في كلّ
   * الباقات. أمّا وحدةٌ تُباع فالغيابُ فيها يعني «لم تُشترَ»: لو قرأناها
   * بـ`hasFeature` لأُعلنت لكلّ الشركات يوم تُضاف إلى الكتالوج، ولرأى عميلٌ
   * بنداً لا خادمَ له. ونظيرُها في الخادم `requirePaidModule` ويفشل مغلقاً كذلك.
   */
  hasPaidFeature: (feature: string) => boolean;
  /**
   * هل للشركة تكاملٌ مضبوطٌ مع Drive8 / ERP؟ — قاعدة المالك (2026-10-01): بلا
   * تكاملٍ لا تظهر أزرارُه ولا ملحقاته (مزامنة، إثراء، مطابقة). الخادمُ يشتقّ
   * `integration_<kind>: false` في `/my-features` من التكامل نفسه. ولا يُعرض
   * شيءٌ قبل وصول المفاتيح، وإلّا لمعت الأزرارُ ثمّ اختفت.
   */
  hasIntegration: (kind: "drive8" | "erp") => boolean;
  isLoaded: boolean;
  /**
   * **تعذّر تحميلُ مصفوفة الصلاحيات** — لا «مسموح» ولا «ممنوع» بل «لا أدري».
   *
   * `canView` تُرجع `true` لمفتاحٍ غائبٍ من المصفوفة (افتراضُ الوحدات القديمة
   * المفتوحة)، فمصفوفةٌ **فارغةٌ لأنّ الطلبَ فشل** تُقرأ «كلُّ شيءٍ مسموح» —
   * وهو بعينه ما يمنعه عقدُ الخصوصيّة: الغيابُ لا يُقرأ منحاً على شاشات
   * الحضور والرواتب والهُويّة. وهذه الرايةُ تفصل الحالتين.
   */
  permsUnavailable: boolean;
  /**
   * `canView` فاشلةً **مغلقةً** — للشاشات الحسّاسة وحدَها.
   *
   * ⚠ **ولا تُبدَّل بها `canView` عامّةً**: عشراتُ الوحدات القديمة مفتوحةٌ
   * افتراضاً، وإغلاقُها كلِّها عند فشلِ جلبٍ عابر يقطع موظّفاً عن عمله لأجل
   * شبكةٍ تعثّرت. فالنطاقُ هو وحدةُ الحضور والرواتب والهُويّة كما يقتضي
   * العقدُ المعتمَد، لا النظامُ كلُّه.
   */
  canViewStrict: (feature: string) => boolean;
  /** إعادةُ محاولةٍ محدودةٍ بضغطة — بلا إعادة تحميل الصفحة ولا حلقةِ تكرار. */
  retryPermissions: () => void;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

/**
 * `principal` = هُويّةُ صاحب الجلسة (معرّف · شركة · دور) لا الدورُ وحدَه.
 *
 * ⚠ **الدورُ ليس هُويّة**: موظّفان كلاهما `agent` — أو مديران في شركتين — لا
 * يختلف عندهما نصُّ الدور، فتبديلُ الجلسة بينهما لا يُعيد تشغيل الجلب إن كان
 * الدورُ وحدَه في قائمة الاعتماد. (والخروجُ ثمّ الدخولُ يمرّ اليومَ بـ`null`
 * فيُعيد التشغيل بالمصادفة — وهو اعتمادٌ على ترتيبِ حالةٍ لا على الهُويّة.)
 */
export function PermissionsProvider(
  { children, role, principal }: { children: React.ReactNode; role: string | null; principal?: string | null },
) {
  const [permissions, setPermissions] = useState<FeaturePermission[]>([]);
  // Per-company module flags ({ key: false } hides a module). Absent ⇒ enabled.
  const [features, setFeatures] = useState<Record<string, boolean>>({});
  const [permsLoaded, setPermsLoaded] = useState(false);
  const [featuresLoaded, setFeaturesLoaded] = useState(false);
  // «تعذّر» حالةٌ ثالثةٌ مستقلّةٌ عن «انتهى التحميل»: الطلبُ انتهى **ولم ينجح**.
  const [permsFailed, setPermsFailed] = useState(false);
  // مفتاحُ إعادةِ المحاولة — تغيُّرُه وحدَه يُعيد تشغيل الجلب، فالإعادةُ بضغطةٍ
  // من المستخدم لا حلقةٌ تلقائيّةٌ تُعيد الطلبَ إلى الأبد على خادمٍ متعثّر.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    // ⚠ **ما قُرئ لحسابٍ سابق لا يُقرأ لحسابٍ لاحق.** الخروجُ ثمّ الدخولُ بحساب
    // آخر يُبقي المصفوفةَ القديمة في الحالة، فيرى الثاني ما مُنح الأوّل حتى
    // يصل جوابُه — ونافذةٌ كهذه تكفي لعرض بياناتٍ لا يملكها. فتُمسح أوّلاً،
    // و`isLoaded` يعود false حتى تصل مصفوفتُه هو.
    setPermissions([]);
    setFeatures({});
    setPermsLoaded(false);
    setFeaturesLoaded(false);
    setPermsFailed(false);
    // ⚠ **وجوابٌ متأخّرٌ لصاحبٍ سابقٍ لا يُكتب على الحاليّ.** الجلبُ بلا إلغاء:
    // شبكةٌ بطيئةٌ عند الأوّل ودخولٌ سريعٌ للثاني يجعل `setPermissions` تقع
    // **بعد** تبديل الهُويّة، فتُرسَم مفاتيحُ الأوّل لمن لا يملكها. والرايةُ
    // تُطفأ في التنظيف فتُهمَل كلُّ كتابةٍ متأخّرة.
    let alive = true;
    if (!role) {
      setPermsLoaded(true);
      setFeaturesLoaded(true);
      return () => { alive = false; };
    }
    const token = localStorage.getItem("waslacrm_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // Role ACL — admins implicitly hold every role permission (no fetch needed).
    if (role === "admin") {
      setPermsLoaded(true);
    } else {
      // ⚠ **ثلاثةُ أوجهٍ للفشل، وفكُّ JSON ناجحٌ ليس إثباتَ صلاحيّة**:
      //  · ردٌّ غيرُ ناجح (500/502/401) — `fetch` لا يرمي عليه، فيُفحص `ok`.
      //  · جسمٌ مشوَّه: `{error: …}` يُفكّ بنجاح وليس مصفوفة.
      //  · رفضُ الطلب (شبكةٌ مقطوعة · إجهاض) — يلتقطه `catch`.
      // والثلاثةُ تُوسَم «تعذّر»، لا «مصفوفةٌ فارغةٌ أي كلُّ شيءٍ مسموح».
      fetch("/api/permissions/my", { headers })
        .then(r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then((data: FeaturePermission[]) => {
          if (!Array.isArray(data)) throw new Error("malformed");
          if (alive) { setPermissions(data); setPermsFailed(false); }
        })
        .catch(() => { if (alive) setPermsFailed(true); })
        .finally(() => { if (alive) setPermsLoaded(true); });
    }

    // Per-company module flags apply to EVERY role, including the company
    // owner/admin, so the dashboard only shows modules the master enabled for
    // this company. Fails open (keep everything) on any error.
    fetch("/api/my-features", { headers })
      .then(r => r.json())
      .then((data: { features?: Record<string, boolean> }) => {
        if (alive && data && typeof data.features === "object" && data.features) setFeatures(data.features);
      })
      .catch(() => {})
      .finally(() => { if (alive) setFeaturesLoaded(true); });
    return () => { alive = false; };
  }, [role, principal, reloadKey]);

  const isLoaded = permsLoaded && featuresLoaded;
  // المديرُ لا يُجلب له شيء (دورُه من الرمز لا من المصفوفة)، فلا «تعذُّر» له —
  // وأسبقيّةُ الإدارة الموثوقة تبقى على عقدها كما كانت.
  const permsUnavailable = role !== "admin" && permsLoaded && permsFailed;

  function retryPermissions(): void {
    setReloadKey(k => k + 1);
  }

  /** فاشلةٌ مغلقةً: ما دامت المصفوفةُ غيرَ متاحةٍ فلا منحَ يُدّعى. */
  function canViewStrict(feature: string): boolean {
    if (permsUnavailable) return false;
    return canView(feature);
  }

  // A module is enabled unless the company explicitly has it turned off.
  function hasFeature(feature: string): boolean {
    return features[feature] !== false;
  }

  // وحدةٌ مدفوعة: `=== true` حصراً. والغيابُ — ومنه فشلُ جلب `/my-features`،
  // إذ يبقى الكائنُ فارغاً — يعني «غير مشتركٍ بها»، لا «مفعّلة».
  function hasPaidFeature(feature: string): boolean {
    return features[feature] === true;
  }

  function hasIntegration(kind: "drive8" | "erp"): boolean {
    return featuresLoaded && features[`integration_${kind}`] !== false;
  }

  function canView(feature: string): boolean {
    if (!hasFeature(feature)) return false; // module disabled for this company
    if (role === "admin") return true;
    const perm = permissions.find(p => p.feature === feature);
    return perm ? perm.canView : true;
  }

  function canEdit(feature: string): boolean {
    if (!hasFeature(feature)) return false;
    if (role === "admin") return true;
    const perm = permissions.find(p => p.feature === feature);
    return perm ? perm.canEdit : true;
  }

  function canDelete(feature: string): boolean {
    if (!hasFeature(feature)) return false;
    if (role === "admin") return true;
    const perm = permissions.find(p => p.feature === feature);
    return perm ? perm.canDelete : false;
  }

  return (
    <PermissionsContext.Provider value={{
      canView, canEdit, canDelete, hasFeature, hasPaidFeature, hasIntegration, isLoaded,
      permsUnavailable, canViewStrict, retryPermissions,
    }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }
  return context;
}
